import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { URLS } from 'shared/urls';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import type { IntakeResults as Results } from 'shared/aip-intake';

dayjs.extend(utc);
dayjs.extend(timezone);

const ET = 'America/New_York';
const course = { id: AIP_COURSE_ID, title: 'AI Playground' };

function meetingUrl(meeting: { meetingId: UUID; title: string }) {
  return URLS.student.meeting({ course, meeting: { id: meeting.meetingId, title: meeting.title } });
}

function callDate(d: Date) {
  return dayjs(d).tz(ET).format('MMMM D');
}

function callDateTime(d: Date) {
  return `${dayjs(d).tz(ET).format('dddd, MMMM D, h:mm a')} ET`;
}

export function IntakeResults({
  results,
  subtitle,
  onRestart,
  onFinish,
}: {
  results: Results;
  subtitle?: string;
  onRestart(): void;
  onFinish(): void;
}) {
  const { recordings, upcoming, nextCall } = results;
  return (
    <div class="flex flex-col gap-4">
      <header class="flex flex-col gap-1 px-1">
        <h2 class="text-2xl font-semibold text-gray-900">
          Start with{' '}
          {recordings.length === 1 ? 'this recording' : `these ${recordings.length} recordings`}
        </h2>
        {subtitle && <p class="text-gray-500">{subtitle}</p>}
      </header>
      <ul class="flex flex-col gap-2.5">
        {recordings.map((r) => (
          <li
            key={r.meetingId}
            class="flex items-center gap-4 rounded-xl border border-gray-200 px-4 py-4"
          >
            <span class="hidden sm:inline-flex shrink-0 items-center justify-center w-11 h-11 rounded-lg bg-[#eef3fc] text-[#003DA6]">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="w-4 h-4">
                <polygon points="7 4 20 12 7 20 7 4" />
              </svg>
            </span>
            <span class="flex flex-col gap-0.5 min-w-0 grow">
              <span class="text-xs font-medium text-gray-500">
                Tuesday call · {callDate(r.scheduledAt)}
              </span>
              <span class="font-semibold text-gray-900">{r.title}</span>
              <span class="text-sm text-gray-600">{r.reason}</span>
            </span>
            <a
              href={meetingUrl(r)}
              class="shrink-0 inline-flex items-center min-h-10 px-4 rounded-full border border-[#003DA6] text-sm font-semibold text-[#003DA6] no-underline hover:bg-[#eef3fc]"
            >
              Watch
            </a>
          </li>
        ))}
      </ul>
      {nextCall && (
        <section class="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl bg-[#003DA6] text-white px-5 py-4">
          <div class="flex flex-col gap-1 grow min-w-0">
            <p class="text-xs font-semibold uppercase tracking-wider text-[#c9dbff]">
              Next live call
            </p>
            <h3 class="text-lg font-semibold">
              {nextCall.title} · {callDateTime(nextCall.scheduledAt)}
            </h3>
            {nextCall.description && <p class="text-sm text-[#e3ecff]">{nextCall.description}</p>}
            {upcoming && (
              <p class="text-sm mt-1">
                <span class="font-semibold">Also for you:</span> {callDate(upcoming.scheduledAt)},{' '}
                {upcoming.title}
                {/[.?!]$/.test(upcoming.title) ? ' ' : '. '}
                {upcoming.reason}
              </p>
            )}
          </div>
          <a
            href={meetingUrl(nextCall)}
            class="shrink-0 inline-flex items-center justify-center min-h-11 px-5 rounded-full bg-[#ffc600] text-gray-900 text-sm font-bold no-underline hover:bg-[#ffd23f]"
          >
            Add to my calendar
          </a>
        </section>
      )}
      <footer class="flex justify-between items-center px-1">
        <button
          type="button"
          class="min-h-11 text-sm font-medium text-gray-600 hover:text-gray-900"
          onClick={onRestart}
        >
          Change my answers
        </button>
        <button
          type="button"
          class="min-h-11 text-sm font-semibold text-[#003DA6] hover:text-[#002d7a]"
          onClick={onFinish}
        >
          Go to AI Playground
        </button>
      </footer>
    </div>
  );
}
